import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem,
  Typography, Snackbar, Alert,
} from '@mui/material';
import { deleteTituloProcessoService } from '../../../../services/core/processos/processosTitulosService';
import { extractResponseErrorMessage } from '../../../../helpers/utils/form.utils';
import useSessaoDialogMutacao from '../../../hooks/useSessaoDialogMutacao';

const MENSAGEM_ERRO_GENERICA = 'Não foi possível excluir o título.';

/**
 * Port de `dialogExcluir()`/`OptionDialogNew` (tela real, `Titulos.jsx`) para
 * MUI v5 — AW2-1034 Etapa 5. Confirmação com seletor de Motivo obrigatório
 * (`motivosExclusaoTitulo`); "Confirmar" desabilitado sem motivo selecionado
 * (`disableConfirm={!motivoExclusao}` na tela real).
 *
 * Guarda de sessão/duplo-clique compartilhada — ver
 * `src/prototipo/hooks/useSessaoDialogMutacao.js` (extraída na AW2-1133
 * Etapa 1, mesmo bloco que existia aqui e em `DialogTitulo.jsx`).
 */
export default function DialogExcluirTitulo({
  open, titulo, idProcesso, motivosExclusaoOptions, onClose, onSucesso,
}) {
  const [motivoExclusao, setMotivoExclusao] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');

  const { sessaoRef, enviandoRef } = useSessaoDialogMutacao(open, idProcesso, () => {
    setMotivoExclusao('');
    setErro('');
    setEnviando(false);
  });

  const onChangeMotivo = (event) => {
    const { value } = event.target;
    setMotivoExclusao(value);
  };

  const onConfirmar = async () => {
    if (enviandoRef.current || !motivoExclusao) {
      return;
    }

    const sessaoAlvo = sessaoRef.current;
    enviandoRef.current = true;
    setEnviando(true);
    setErro('');

    try {
      await deleteTituloProcessoService(idProcesso, titulo.id, motivoExclusao);
      if (sessaoRef.current !== sessaoAlvo) {
        return;
      }
      onSucesso();
      onClose();
    } catch (exception) {
      if (sessaoRef.current !== sessaoAlvo) {
        return;
      }
      setErro(extractResponseErrorMessage(exception) || MENSAGEM_ERRO_GENERICA);
    } finally {
      if (sessaoRef.current === sessaoAlvo) {
        enviandoRef.current = false;
        setEnviando(false);
      }
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>Excluir Título</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {`Confirma a exclusão do título${titulo?.numero ? ` nº ${titulo.numero}` : ''}?`}
          </Typography>
          <TextField
            select
            fullWidth
            label="Motivo*"
            value={motivoExclusao}
            onChange={onChangeMotivo}
            size="small"
          >
            {motivosExclusaoOptions.map(opcao => (
              <MenuItem key={opcao.id} value={opcao.id}>{opcao.value}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={enviando}>Cancelar</Button>
          <Button onClick={onConfirmar} variant="contained" color="error" disabled={!motivoExclusao || enviando}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(erro)}
        autoHideDuration={4000}
        onClose={() => setErro('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setErro('')}>
          {erro}
        </Alert>
      </Snackbar>
    </>
  );
}

DialogExcluirTitulo.propTypes = {
  open: PropTypes.bool,
  titulo: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    numero: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  idProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  motivosExclusaoOptions: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  onSucesso: PropTypes.func.isRequired,
};

DialogExcluirTitulo.defaultProps = {
  open: false,
  titulo: null,
  idProcesso: null,
  motivosExclusaoOptions: [],
};
